import { createSelector } from 'reselect';
import { formatTime } from 'helpers/formatters';
import { getLocale } from 'actions/language/selectors';
import { getNestedObj } from 'helpers/getNestedObject';
import { updateFields } from 'conf/userFields';

export const getCurrentPage = (state) => state.users.currentPage;
export const getLastPaginationPage = createSelector(
  getCurrentPage,
  (currentPage) => currentPage + 4
);
export const getFirstPaginationPage = createSelector(
  getCurrentPage,
  (currentPage) => currentPage - 4
);
export const getIsModalOpen = (state) => state.users.isModalOpen;

export const getTotalPages = (state) => state.users.totalPages;
export const getLimit = (state) => state.users.limit;
export const getMembers = createSelector(
  [(state) => state.users.members, getLimit, getCurrentPage, getLocale],
  (members, limit, currentPage, lang) =>
    members.map((member, index) => ({
      ...member,
      user_group: {
        ...member.user_group,
        user_group_id: member.user_group.id
      },
      phone_number: {
        phone_number: member.phone_number,
        calling_code: member.phone_number_country_code
      },
      policies:
        member.policies &&
        member.policies.map(({ id, name }) => ({
          name: name,
          value: id
        })),
      displayIndex: currentPage * limit + index + 1,
      created_at: formatTime(member.created_at, lang),
      updated_at: formatTime(member.updated_at, lang)
    }))
);
export const getTotal = (state) => state.users.total;
export const getLimitDropdownOpen = (state) => state.users.limitDropdownOpen;
export const getMember = createSelector(
  (state, props) => state.users.members[props.index],
  (member) => ({
    ...member,
    user_group: {
      ...member.user_group,
      user_group_id: member.user_group.id
    },
    phone: {
      phone_number: member.phone_number,
      calling_code: member.phone_number_country_code
    },
    policies: member.policies.map(({ id, name }) => ({
      name: name,
      value: id
    }))
  })
);
export const getEditData = (state) => state.users.editData;
export const getSearchFieldDropdownOpen = (state) =>
  state.users.searchFieldDropdownOpen;
export const getSearch = (state) => state.users.search;
export const getAllPoliciesFormatted = (state) => {
  const policies = state.users.shouldFilterOutSHAWPolicy
    ? state.users.allPolicies.filter(
        (policy) => policy.key !== 'shaw_analytics'
      )
    : state.users.allPolicies;

  return policies.map((policy) => ({
    name: policy.name,
    value: policy.id
  }));
};
/**
 * The policies that can access SHAW. Note that 'administrator' only has shaw access on root groups.
 *
 * @param {object} state
 * @return {Array} An array of strings
 */
export const getAllShawPolicies = (state) =>
  state.users.allPolicies.filter(
    (policy) => policy.key == 'administrator' || policy.key == 'shaw_analytics'
  );
/**
 * Not all root groups have the SHAW product. We filter policies on the backend if they don't.
 * We also filter on some client side logic.
 *
 * @param {object} state
 * @return {boolean}
 */
export const isShawPolicyAvailableToClient = (state) =>
  !state.users.shouldFilterOutSHAWPolicy &&
  state.users.allPolicies.some((policy) => policy.key == 'shaw_analytics');

export const getUpdatedPolicyIds = (_, updatedUserData) => {
  if (updatedUserData && updatedUserData.policies) {
    return updatedUserData.policies.map((item) => item.value);
  }
  return null;
};

export const getUserId = (state, props) => state.users.members[props.index].id;
export const getUserGroupSuggestions = (state) =>
  state.users.userGroupSuggestions || [];
export const getFormattedUserGroupSuggestions = createSelector(
  [getUserGroupSuggestions],
  (suggestions) =>
    suggestions.length > 0
      ? suggestions.map(({ id, group_code, name }) => ({
          label: group_code,
          name,
          value: id
        }))
      : suggestions
);

export const getCountriesByCode = (state) => {
  let countryList = [{ defaultOption: true, value: '' }];

  state.users.countries.forEach((country) => {
    countryList.push({
      name: country.iso_3166_1_alpha_3,
      value: country.country_code
    });
  });

  return countryList;
};
export const getCountriesByName = (state) => {
  let countryList = [{ defaultOption: true, value: '' }];

  state.users.countries.forEach((country) => {
    countryList.push({
      name: country.name,
      value: country.country_code
    });
  });

  return countryList;
};
export const getDialCodeCountryList = (state) => {
  let countryCodeList = [];
  let countriesData = {};
  state.users.countries.forEach((countryData) => {
    let countryCode = countryData.country_code.toLowerCase();
    countryCodeList.push(countryCode);
    countriesData[countryCode] = countryData.name;
  });
  return { list: countryCodeList, data: countriesData };
};

export const getUpdateUserRequest = (state, props) => {
  let member = getMember(state, props);

  const { updatedUserData } = props;

  if (!updatedUserData || Object.keys(updatedUserData).length === 0) {
    return null;
  }
  let request = {};

  updateFields.forEach((field) => {
    let value = getNestedObj(updatedUserData, field.keyPath);
    typeof value !== 'undefined'
      ? (request[field.key] = value)
      : (request[field.key] = getNestedObj(member, field.keyPath));
  });
  return request;
};



// WEBPACK FOOTER //
// ./src/actions/accounts/users/usersSelectors.js